import { postTrackingData } from "@/api/endpoints/track";
import { SOURCE_CODE_TO_LABEL } from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import { EVENT } from "@/tracking_events";
import { Mention } from "@/types/Mention";
import AccessControl from "@components/AccessControl";
import { Button } from "@components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";

export default function MentionRow({
  mention,
  onMarkRelevant,
}: {
  mention: Mention;
  onMarkRelevant: (public_id: Mention["public_id"]) => Promise<unknown>;
}) {
  const { formatDate } = useI18n();

  return (
    <Card size="sm">
      <CardHeader>
        <CardDescription>
          {i18n._(SOURCE_CODE_TO_LABEL[mention.source])} ·{" "}
          {formatDate(mention.episode_published_date, "PP")}
        </CardDescription>
        <CardTitle>
          {mention.channel_name}: {mention.episode_title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p
          data-testid="mention-text-content"
          className="mention text-sm leading-6 text-balance highlight-match"
          dangerouslySetInnerHTML={{
            __html: mention.trimmed_content,
          }}
        />
      </CardContent>
      <AccessControl>
        <CardFooter className="pt-2 gap-4">
          <Button
            id={`mark-relevant-${mention.mention_pk}`}
            size="sm"
            variant="outline"
            onClick={() => {
              if (mention.marked_irrelevant_reason === "llm_irrelevant") {
                postTrackingData(EVENT.HIDDEN_MENTION_LLM_IRRELEVANT_SHOWN, {
                  mention: mention.public_id,
                });
              }
              onMarkRelevant(mention.public_id);
            }}
          >
            <Trans>Visa</Trans>
          </Button>

          <div className="flex items-center divide-x *:px-3 *:first:pl-0 *:last:pr-0">
            {mention.marked_irrelevant_at && (
              <p className="text-sm text-muted-foreground">
                <Trans>
                  Borttaget {formatDate(mention.marked_irrelevant_at, "PPp")}
                </Trans>
              </p>
            )}

            {mention.marked_irrelevant_reason && (
              <p className="text-sm text-muted-foreground">
                {mention.marked_irrelevant_reason === "llm_irrelevant" ? (
                  <Trans>Automatiskt bortfiltrerad</Trans>
                ) : (
                  <Trans>Anledning: {mention.marked_irrelevant_reason}</Trans>
                )}
              </p>
            )}

            {mention.marked_irrelevant_by && (
              <p
                data-testid="marked_irrelevant_by"
                className="text-sm text-muted-foreground"
              >
                <Trans>Borttaget av {mention.marked_irrelevant_by}</Trans>
              </p>
            )}
          </div>
        </CardFooter>
      </AccessControl>
    </Card>
  );
}
